import { useMemo } from "react";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import {
    TransactionColumn,
    TransactionView,
} from "@common/types/views/transactionViews";
import { useNonLockedList } from "@hooks/useNonLockedList";

/**
 * Drops `LOCK` and `SPLIT` from the rendered view when no row in the current
 * page actually needs them — they're pinned-left, 32–40px wide, and empty
 * for the vast majority of rows. In the narrow task-body viewport their
 * emptiness reads as a gap between checkbox / Date / next content column.
 * Mirrors the lock check from `useNonLockedList` (entity lock dates) and
 * the split check from `SplitCell` (`splitParentId != null`), so the column
 * pops back in as soon as a row in view actually has a lock or a split.
 *
 * IMPORTANT: render the result, never save it. Persisting the stripped column
 * list back to `currentView` (which ColumnSettings does on density / reorder /
 * toggle interactions, and the table does on resize) would drop LOCK / SPLIT
 * from the canonical state, and this hook can only filter existing columns,
 * not re-add them — so the columns would never reappear even once a locked /
 * split transaction loads. `TransactionsTaskView` applies it and runs every
 * table view change through `restoreHiddenColumns` before it's saved.
 */
export function useConditionallyHiddenColumns(
    view: TransactionView,
    transactions: TransactionListItem[] | undefined,
): TransactionView {
    const nonLockedTransactions = useNonLockedList(transactions);
    const hasLockedTransaction =
        (transactions?.length ?? 0) > nonLockedTransactions.length;

    const hasSplitTransaction = useMemo(
        () => !!transactions?.some((t) => t.splitParentId != null),
        [transactions],
    );

    return useMemo(() => {
        const hiddenColumnIds = new Set<string>();
        if (!hasLockedTransaction) {
            hiddenColumnIds.add(TransactionColumn.LOCK);
        }
        if (!hasSplitTransaction) {
            hiddenColumnIds.add(TransactionColumn.SPLIT);
        }
        if (hiddenColumnIds.size === 0) {
            return view;
        }
        return {
            ...view,
            columns: view.columns.filter(
                (column) => !hiddenColumnIds.has(column.id),
            ),
        };
    }, [view, hasLockedTransaction, hasSplitTransaction]);
}
